import React, { useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import config from '../../config.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/AutoSuggest.css';

const UserAutoSuggest = ({ 
  placeholder,
  value = "",
  onChange,
  onUserSelect,
  className = "",
  disabled = false,
  excludeUserIds = [], // Array of user IDs to exclude from results (e.g., current moderators)
  bandId: bandIdProp
}) => {
  const { bandid } = useParams();
  const bandId = bandIdProp ?? bandid;
  const fetchWrapper = useFetchWrapper();
  const t = useT();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const searchUsers = useCallback(async (searchTerm) => {
    if (!bandId || !searchTerm || searchTerm.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    try {
      // Only the band's own followers can be found (ENG-473): the backend
      // has no user directory. Each match is { id, username, displayName,
      // profileImageUrl }.
      const response = await fetchWrapper.get(
        `${config.apiBaseUrl}/bands/${bandId}/followers/search?q=${encodeURIComponent(searchTerm.trim())}&limit=10`
      );
      const users = response && Array.isArray(response.users) ? response.users : [];

      // Filter out excluded users (current moderators, band members)
      const excluded = new Set(excludeUserIds.map(String));
      const filteredUsers = users.filter(user => !excluded.has(String(user.id)));

      setSuggestions(filteredUsers);
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error searching users:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  }, [fetchWrapper, excludeUserIds, bandId]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange && onChange(e);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce search
    debounceRef.current = setTimeout(() => {
      searchUsers(newValue);
    }, 300);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleUserSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleUserSelect = (user) => {
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    
    // Clear the input
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    
    onUserSelect && onUserSelect(user);
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow for click events
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const handleFocus = () => {
    if (suggestions.length > 0 && value.length >= 2) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="auto-suggest-container">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder ?? t('moderators.userSearch.placeholder')}
        className={`auto-suggest-input ${className}`}
        disabled={disabled}
        autoComplete="off"
      />
      
      {loading && (
        <div className="auto-suggest-loading">
          {t('moderators.userSearch.searching')}
        </div>
      )}
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="auto-suggest-dropdown">
          {suggestions.map((user, index) => (
            <div
              key={user.id}
              className={`auto-suggest-item ${selectedIndex === index ? 'selected' : ''}`}
              onClick={() => handleUserSelect(user)}
            >
              <div className="user-suggest-info">
                <div className="user-suggest-username">
                  {user.username}
                </div>
                {user.displayName && user.displayName !== user.username && (
                  <div className="user-suggest-name">
                    {user.displayName}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {showSuggestions && suggestions.length === 0 && !loading && value.length >= 2 && (
        <div className="auto-suggest-dropdown">
          <div className="auto-suggest-item no-results">
            {t('moderators.userSearch.noResults')}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAutoSuggest;